import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useStore } from "zustand";
import assert from "assert";
import { isDefined } from "utils/isDefined";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import {
  ProjectTablesTableStore,
  ProjectTablesTableStoreState,
} from "./ProjectTablesTableProvider.types";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { createProjectTablesTableStore } from "./ProjectTablesTableProvider.helpers";
import { useProjectFolderContext } from "pages/gdt/folders/ProjectFoldersProvider";

const ProjectTablesTableContext = createContext<
  ProjectTablesTableStore | undefined
>(undefined);

interface ProjectTablesTableProviderProps {
  children: ReactNode;
}

export const ProjectTablesTableProvider = ({
  children,
}: ProjectTablesTableProviderProps) => {
  const tables = useDataTableContext((s) => s.tables);
  const project = useDataTableContext((s) => s.project);
  const folders = useProjectFolderContext((s) => s.folders);
  const updateAnalysisTable = useDataTableContext((s) => s.updateAnalysisTable);
  const updateDataTable = useDataTableContext((s) => s.updateDataTable);
  const STORAGE_KEY_BASE = `project-${project.id}`;

  const [store] = useState(() =>
    createProjectTablesTableStore({
      STORAGE_KEY_BASE,
      folders,
      tables,
      updateAnalysisTable,
      updateDataTable,
    }),
  );

  // sync data table context with project tables table context
  // need to keep a copy of the tables in the store so we can
  // filter and sort them
  useEffect(() => {
    store.getState().setTables(tables);
  }, [tables, store]);

  useEffect(() => {
    store.setState((state) => {
      state.folders = [...folders];
      return state;
    });
  }, [folders, store]);

  return (
    <ProjectTablesTableContext.Provider value={store}>
      {children}
    </ProjectTablesTableContext.Provider>
  );
};

/** Hook for consuming the {@link ProjectTablesTableContext} */
export const useProjectTablesTableContext = <T,>(
  selector: (state: ProjectTablesTableStoreState) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => {
  const store = useContext(ProjectTablesTableContext);
  assert(
    isDefined(store),
    new ContextOutOfBoundsError("ProjectTablesTableContext"),
  );
  return useStore(store, selector, equalityFn);
};
